// ============================================================
// BLOG CTA CARD - card de oferta dentro do post
// ============================================================
// CSS proprio, com escopo (.bcta), fora do Tailwind: o blog tem regras globais
// sem @layer (h3, p, a) que venciam as utilities do Tailwind v4 e deixavam o
// texto escuro e o botao rosa sublinhado (11/10/2026).
// Layout por container query: decide pela largura da COLUNA do post (568 px no
// desktop), nao da tela. Antes, a partir de 768 px de tela o card abria 3
// colunas dentro de 568 px e cortava preco e botao.
//   coluna estreita: imagem em cima, texto, faixa de preco + botao
//   coluna >= 520 px: imagem a esquerda, texto e faixa a direita
//   imagem larga (wide, ex.: varios livros lado a lado): sempre em cima
// ============================================================

import type { CSSProperties } from 'react'

export interface BlogCtaCardProps {
  /** Texto pequeno acima do titulo, em caps */
  badge?: string
  /** Titulo grande do CTA */
  title: string
  /** Descricao em 1-2 linhas */
  description: string
  /** Preco (ex: "R$ 19,90") */
  price?: string
  /** Texto pequeno abaixo do preco (ex: "Pagamento unico") */
  priceCaption?: string
  /** Texto do botao */
  buttonText: string
  /** URL do botao */
  href: string
  /** Imagem opcional (capa de livro, documento, mockup), de preferencia com fundo transparente */
  mockupSrc?: string
  /** Alt text da imagem */
  mockupAlt?: string
  /** Tamanho da imagem: sm (icone), md, lg (capa) ou wide (imagem larga, vai em cima do texto) */
  mockupSize?: 'sm' | 'md' | 'lg' | 'wide'
  /** Esquema de cores. Default: teal */
  variant?: 'teal' | 'slate' | 'blue' | 'amber' | 'emerald' | 'purple' | 'rose' | 'indigo'
}

// fundo do card e cor do texto do botao (branco) por variante
const VARIANTS: Record<NonNullable<BlogCtaCardProps['variant']>, { bg: string; ink: string }> = {
  teal: { bg: 'linear-gradient(135deg, #0d9488 0%, #0f766e 50%, #065f46 100%)', ink: '#0f766e' },
  slate: { bg: 'linear-gradient(135deg, #334155 0%, #1e293b 55%, #1c1917 100%)', ink: '#1e293b' },
  blue: { bg: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 50%, #312e81 100%)', ink: '#1d4ed8' },
  amber: { bg: 'linear-gradient(135deg, #d97706 0%, #c2410c 55%, #991b1b 100%)', ink: '#b45309' },
  emerald: { bg: 'linear-gradient(135deg, #059669 0%, #047857 50%, #115e59 100%)', ink: '#047857' },
  purple: { bg: 'linear-gradient(135deg, #9333ea 0%, #7e22ce 50%, #701a75 100%)', ink: '#7e22ce' },
  rose: { bg: 'linear-gradient(135deg, #e11d48 0%, #be123c 50%, #831843 100%)', ink: '#be123c' },
  indigo: { bg: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 50%, #1e3a8a 100%)', ink: '#4338ca' },
}

// largura maxima da imagem por tamanho (a altura segue a proporcao)
const MOCKUP_MAX = { sm: 96, md: 140, lg: 170, wide: 560 }

const CSS = `
.bcta{container-type:inline-size;margin:44px auto;max-width:760px;clear:both}
.bcta .bcta-card{position:relative;overflow:hidden;isolation:isolate;border-radius:24px;padding:24px;color:#fff;background:var(--bcta-bg);box-shadow:0 24px 48px -24px rgba(15,10,30,.55)}
.bcta .bcta-card::before,.bcta .bcta-card::after{content:"";position:absolute;z-index:-1;width:260px;height:260px;border-radius:50%;background:rgba(255,255,255,.10);filter:blur(40px)}
.bcta .bcta-card::before{top:-110px;right:-90px}
.bcta .bcta-card::after{bottom:-130px;left:-100px;background:rgba(0,0,0,.14)}
.bcta .bcta-grid{display:grid;grid-template-columns:1fr;gap:20px;align-items:center}
.bcta .bcta-mockup{justify-self:center;width:100%;max-width:min(var(--bcta-mock),62%);line-height:0}
.bcta .bcta-larga .bcta-mockup{max-width:100%}
.bcta .bcta-larga .bcta-mockup img{max-height:190px;margin:0 auto}
.bcta .bcta-mockup img{display:block;width:100%;height:auto;max-height:240px;object-fit:contain;margin:0;border:0;border-radius:0;box-shadow:none;background:none;filter:drop-shadow(0 14px 22px rgba(0,0,0,.28))}
.bcta .bcta-body{min-width:0}
.bcta .bcta-badge{display:inline-block;margin:0 0 12px;padding:6px 11px;border-radius:999px;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.28);color:#fff;font:700 11px/1.2 var(--font-body,system-ui,sans-serif);letter-spacing:.12em;text-transform:uppercase}
.bcta .bcta-title{margin:0 0 10px;color:#fff;font:800 clamp(22px,4.8cqi,28px)/1.15 var(--font-display,system-ui,sans-serif);letter-spacing:-.01em;text-wrap:balance}
.bcta .bcta-desc{margin:0 0 20px;color:rgba(255,255,255,.9);font:400 16px/1.55 var(--font-body,system-ui,sans-serif)}
.bcta .bcta-acao{display:flex;flex-wrap:wrap;align-items:center;gap:14px 20px;padding-top:18px;border-top:1px solid rgba(255,255,255,.22)}
.bcta .bcta-preco{display:flex;flex-direction:column;gap:4px;margin:0}
.bcta .bcta-preco strong{color:#fff;font:800 clamp(22px,5cqi,26px)/1.1 var(--font-display,system-ui,sans-serif);text-wrap:balance}
.bcta .bcta-preco span{color:rgba(255,255,255,.78);font:500 12px/1.3 var(--font-body,system-ui,sans-serif)}
.bcta a.bcta-btn,.bcta a.bcta-btn:visited{margin-left:auto;display:inline-flex;align-items:center;justify-content:center;gap:8px;padding:14px 20px;border-radius:12px;background:#fff;color:var(--bcta-ink);font:700 15px/1.2 var(--font-body,system-ui,sans-serif);text-decoration:none;text-align:center;box-shadow:0 10px 20px -10px rgba(0,0,0,.45);transition:transform .15s ease,box-shadow .15s ease}
.bcta a.bcta-btn:hover{color:var(--bcta-ink);text-decoration:none;transform:translateY(-1px);box-shadow:0 14px 24px -10px rgba(0,0,0,.5)}
.bcta a.bcta-btn:focus-visible{outline:3px solid #fff;outline-offset:3px}
.bcta a.bcta-btn svg{width:16px;height:16px;flex:none}
@container (max-width: 459px){
  .bcta .bcta-acao{flex-direction:column;align-items:stretch}
  .bcta a.bcta-btn{margin-left:0;width:100%}
}
@container (min-width: 520px){
  .bcta .bcta-card{padding:32px}
  .bcta .bcta-grid.bcta-com-imagem{grid-template-columns:minmax(110px,var(--bcta-mock)) minmax(0,1fr);gap:28px}
  .bcta .bcta-com-imagem .bcta-mockup{max-width:none}
}
@media (prefers-reduced-motion: reduce){.bcta a.bcta-btn{transition:none}.bcta a.bcta-btn:hover{transform:none}}
`

export default function BlogCtaCard({
  badge,
  title,
  description,
  price,
  priceCaption,
  buttonText,
  href,
  mockupSrc,
  mockupAlt = '',
  mockupSize = 'md',
  variant = 'teal',
}: BlogCtaCardProps) {
  const v = VARIANTS[variant] ?? VARIANTS.teal
  const estilo = {
    '--bcta-bg': v.bg,
    '--bcta-ink': v.ink,
    '--bcta-mock': `${MOCKUP_MAX[mockupSize] ?? MOCKUP_MAX.md}px`,
  } as CSSProperties

  return (
    <aside className="bcta not-prose" style={estilo} aria-label={badge ? `${badge}: ${title}` : title}>
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <div className="bcta-card">
        <div className={`bcta-grid${mockupSrc ? (mockupSize === 'wide' ? ' bcta-larga' : ' bcta-com-imagem') : ''}`}>
          {mockupSrc && (
            <div className="bcta-mockup">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={mockupSrc} alt={mockupAlt} loading="lazy" decoding="async" />
            </div>
          )}
          <div className="bcta-body">
            {badge && <span className="bcta-badge">{badge}</span>}
            <p className="bcta-title">{title}</p>
            <p className="bcta-desc">{description}</p>
            <div className="bcta-acao">
              {price && (
                <p className="bcta-preco">
                  <strong>{price}</strong>
                  {priceCaption && <span>{priceCaption}</span>}
                </p>
              )}
              <a className="bcta-btn" href={href}>
                {buttonText}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}
